// src/store/incidentStore.js
// Operational incident store.
// Incidents are distinct from feed posts — they are tracked, updateable,
// and have lifecycle state (unverified → active → mitigating → resolved).
//
// Three slices:
//   useIncidentStore  — live incident data (not persisted)
//   useIncidentActions — write operations (wired to sync engine)
//
// Data flow:
//   ingestion pipelines → setIncidents()
//   community confirm  → confirmIncident() → increments counter
//   new update posted  → addUpdate()
//   status change      → updateStatus()
//   SWR + ingestion    → merge()

import { create } from 'zustand';
import { getIncidentIndex, rebuildIndex } from '@services/geolocation/spatialIndex';
import {
  createIncident,
  createIncidentUpdate,
  createConfirmation,
  escalateFromReport,
  INCIDENT_STATUS,
  EVACUATION_STATUS,
} from '@data/schemas/Incident';

// ── useIncidentStore ───────────────────────────────────────────────
// Not persisted: incidents are always re-fetched from Firestore.
// The goal is fast optimistic local updates with server reconciliation.
export const useIncidentStore = create((set, get) => ({

  // ── State ─────────────────────────────────────────────────────
  incidents:    [],         // Incident[]
  updates:      {},         // { [incidentId]: IncidentUpdate[] }
  confirmations:{},         // { [incidentId]: Set<userId> } — local device only
  loading:      false,
  lastUpdated:  null,
  error:        null,

  // ── Feed ingestion ────────────────────────────────────────────
  // Replace the full incident list (called after SWR fetch).
  setIncidents: (incidents) => set({
    incidents,
    lastUpdated: Date.now(),
    loading:     false,
    error:       null,
  }),

  // Merge server incidents into local state.
  // Keeps optimistic local changes for incidents not yet server-confirmed.
  mergeIncidents: (incoming) => set((s) => {
    const byId = Object.fromEntries(s.incidents.map((i) => [i.incidentId, i]));
    for (const inc of incoming) {
      byId[inc.incidentId] = {
        ...(byId[inc.incidentId] ?? {}),
        ...inc,
      };
    }
    const merged = Object.values(byId);
    // Keep spatial index in sync — rebuild on every merge
    // Map incidentId → id so SpatialIndex can use its standard id field
    rebuildIndex(getIncidentIndex(), merged.filter((i) => i.geometry?.lat).map((i) => ({
      id:  i.incidentId,
      lat: i.geometry.lat,
      lon: i.geometry.lon,
      status:   i.status,
      severity: i.severity,
      type:     i.type,
    })));
    return { incidents: merged, lastUpdated: Date.now() };
  }),

  // ── Single incident mutations (all optimistic) ────────────────
  // Optimistically update status — synced to Firestore by incidentActions.updateStatus()
  updateStatus: (incidentId, status, evacuationStatus) => set((s) => ({
    incidents: s.incidents.map((i) =>
      i.incidentId !== incidentId ? i : {
        ...i,
        status,
        evacuationStatus: evacuationStatus ?? i.evacuationStatus,
        updatedAt: new Date().toISOString(),
      }
    ),
  })),

  // Optimistic confirm — immediately increments counter locally
  confirmIncident: (incidentId, vote = 'confirm') => set((s) => {
    const field = vote === 'confirm' ? 'confirmCount' : 'denyCount';
    const userKey = `${incidentId}`;
    const alreadyVoted = (s.confirmations[userKey] ?? new Set()).has(vote);
    if (alreadyVoted) return s;  // idempotent

    const newConfirmations = {
      ...s.confirmations,
      [userKey]: new Set([...(s.confirmations[userKey] ?? []), vote]),
    };

    return {
      confirmations: newConfirmations,
      incidents: s.incidents.map((i) =>
        i.incidentId !== incidentId ? i : {
          ...i,
          [field]: (i[field] ?? 0) + 1,
        }
      ),
    };
  }),

  // Prepend an update to the timeline (optimistic)
  addUpdate: (incidentId, update) => set((s) => ({
    updates: {
      ...s.updates,
      [incidentId]: [update, ...(s.updates[incidentId] ?? [])],
    },
    // If update carries a status change, apply it
    incidents: update.statusChange
      ? s.incidents.map((i) =>
          i.incidentId !== incidentId ? i : {
            ...i,
            status:           update.statusChange,
            evacuationStatus: update.evacuationChange ?? i.evacuationStatus,
            updatedAt:        update.createdAt,
          }
        )
      : s.incidents,
  })),

  // Load updates for one incident (called when detail view opens)
  setUpdates: (incidentId, updates) => set((s) => ({
    updates: { ...s.updates, [incidentId]: updates },
  })),

  // ── Escalation (UserReport → Incident) ───────────────────────
  // When a community report crosses the confirmation threshold,
  // it can be escalated into a structured incident.
  escalateReport: (report) => {
    const incident = escalateFromReport(report);
    if (!incident) return null;

    set((s) => {
      // Don't duplicate if already escalated
      const exists = s.incidents.some(
        (i) => i.originReportId === report.id
      );
      if (exists) return s;
      return { incidents: [incident, ...s.incidents] };
    });

    return incident;
  },

  // ── Derived selectors ─────────────────────────────────────────
  // These don't need to be memoized here — use in hooks with useMemo if needed
  getActive:    () => get().incidents.filter((i) => ['active', 'mitigating'].includes(i.status)),
  getCritical:  () => get().incidents.filter((i) => i.severity >= 4),
  getByType:    (type)    => get().incidents.filter((i) => i.type === type),
  getByParish:  (parish)  => get().incidents.filter((i) => i.parish === parish || (i.affectedAreas ?? []).includes(parish)),
  getById:      (id)      => get().incidents.find((i) => i.incidentId === id) ?? null,
  getUpdates:   (id)      => get().updates[id] ?? [],

  // Incidents with evacuation orders active
  getEvacuationOrders: () => get().incidents.filter(
    (i) => ['mandatory', 'warning'].includes(i.evacuationStatus) && i.status !== 'resolved'
  ),

  setLoading: (loading) => set({ loading }),
  setError:   (error)   => set({ error, loading: false }),
}));
